import { ApiData, Excess } from 'scenes/common/types';
import {
  MotorFunnelState,
  MotorQuoteDetails,
  MarketAgreedValue,
  OptionalExtra,
  QuoteUserSelections,
  MotorCoverType,
  ExcessType,
  MotorInsuranceType,
  MotorQuoteData,
} from 'scenes/motor/types';
import {
  WireCoverageOptionType,
  WireCoverType,
  WireNameValue,
  WireProduct,
  WireQuoteRequest,
  WireStatus,
} from 'scenes/common/api/quote/types';
import { toMainAddress } from 'scenes/common/api/quote/marshaller/address';
import {
  getInitialMetaData,
  getPolicyDetailsMetadata,
  getQuoteCompleteMetadata,
} from 'scenes/common/api/quote/marshaller/metadata';
import {
  requestDefaults,
  toWireFlybuys,
  toFlybuysNumber,
  toHasFlybuys,
} from 'scenes/common/api/quote/marshaller';

import { makeApiSuccess, getApiData } from 'scenes/common/helpers/apiData';
import {
  isFlybuysMember,
  getVehicleManuallyEntered,
  getVehicleFromSearch,
  getSelectedCoverType,
  getSelectedExtras,
  getSelectedInsuranceType,
  isUnder25CoverSelected,
  getFlybuysNumber,
  isVehicleManualEntry,
  getPolicyStartDate,
} from 'scenes/motor/selectors';
import { ReviewDetailsData } from 'scenes/motor/flows/reviewDetails';
import { wireCoverageId } from 'scenes/common/api/quote/marshaller/coverage';
import { toWireSaveQuoteEmailAddress } from 'scenes/common/api/quote/marshaller/policy';
import { unmarshalCampaigns } from 'scenes/common/api/quote/marshaller/campaign';

import {
  WireMotorQuoteCreateRequest,
  WireMotorQuotePayload,
  WireMotorQuoteResponse,
  WireMotorQuoteUpdateRequest,
  WireMotorExcess,
} from '../types';
import { toWireAddresses } from './address';
import {
  lookupToWireVehicle,
  manualToWireVehicle,
  unmarshalVehicleValueRange,
  unmarshalVehicleValue,
  unmarshalMarketOrAgreedValue,
  unmarshalVehicleModifications,
  toIsUsedForBusiness,
  toBusinessType,
  toVehicleSearchType,
  toLicencePlate,
  toVehicleFromSearch,
  toVehicleManualSelectFields,
  unmarshalVehicleMarketValue,
  toVehicleManuallyEntered,
} from './vehicle';
import {
  toWireCoverages,
  wireSumInsuredType,
  unmarshalInsuranceType,
  wireCoverageRefIds,
  wireCoverType,
  unmarshalInsuranceOptions,
} from './coverage';
import {
  toWireMotorPolicy,
  toSmallBusiness,
  toPolicyStartDate,
  toCoverLevel,
  toValidPolicyStartDate,
  toRecentInsurer,
} from './policy';

import {
  toWireMotorParties,
  toPolicyDetailsParties,
  mapMainDriver,
  toAdditionalDrivers,
  toAddDriver,
} from './party';
import { findSelectedExtras, toCoverForDriversUnder25 } from './premium';
import { unmarshalExcesses, unmarshalSelectedExcess } from './excess';
import { toPromoCode } from './campaign';
import { toFinance, toVehicleFinancier } from './finance';
import { toOptionalExtra } from './options';

const optionalExtras = (state: MotorFunnelState): string[] => {
  const extras = [...getSelectedExtras(state)];

  const under25Index = extras.indexOf(OptionalExtra.Under25);

  if (under25Index > -1 && !isUnder25CoverSelected(state)) {
    extras.splice(under25Index, 1);
  } else if (under25Index < 0 && isUnder25CoverSelected(state)) {
    extras.push(OptionalExtra.Under25);
  }

  return extras;
};

export const marshalQuoteCreateRequest = (
  state: MotorFunnelState
): WireMotorQuoteCreateRequest => ({
  ...requestDefaults(WireProduct.Motor),
  metaData: getInitialMetaData(),
  flybuys: toWireFlybuys(isFlybuysMember(state), getFlybuysNumber(state)),
  coverType: wireCoverType[getSelectedCoverType(state)],
  policy: toWireMotorPolicy(state, getPolicyStartDate(state)),
  parties: toWireMotorParties(state),
  addresses: toWireAddresses(state),
  vehicle: isVehicleManualEntry(state)
    ? manualToWireVehicle(
        state,
        getVehicleManuallyEntered(state),
        wireCoverageRefIds[getSelectedInsuranceType(state)]
      )
    : lookupToWireVehicle(
        state,
        getVehicleFromSearch(state),
        wireCoverageRefIds[getSelectedInsuranceType(state)]
      ),
  coverages: toWireCoverages(
    state,
    getSelectedInsuranceType(state),
    optionalExtras(state)
  ),
});

export const unmarshalQuoteResponse = (
  response: WireMotorQuoteResponse
): ApiData<MotorQuoteDetails> =>
  makeApiSuccess<MotorQuoteDetails>({
    payload: response.payload,
    agreedVehicleValueRange: unmarshalVehicleValueRange(response.payload),
    vehicleMarketValue: unmarshalVehicleMarketValue(response.payload),
    insuranceOptions: unmarshalInsuranceOptions(response.payload),
    excesses: unmarshalExcesses(response.payload),
    vehicleModifications: unmarshalVehicleModifications(response.payload),

    userSelections: {
      agreedVehicleValue: unmarshalVehicleValue(response.payload),
      excess: unmarshalSelectedExcess(response.payload),
      marketOrAgreedValue: unmarshalMarketOrAgreedValue(response.payload),
      insuranceType: unmarshalInsuranceType(response.payload),
    },

    campaigns: unmarshalCampaigns(response.payload),
  });

const marshalQuoteUpdateRequest = (
  state: MotorFunnelState,
  {
    excess: selectedExcess,
    marketOrAgreedValue,
    agreedVehicleValue,
    insuranceType: selectedInsuranceType,
  }: QuoteUserSelections,
  selectedExtras: string[],
  selectedCoverType: MotorCoverType,
  policyStartDate: string
): WireMotorQuoteUpdateRequest => {
  const { payload } = getApiData(state.apiResponses.quoteDetails);

  const excessUpdate = selectedExcess
    ? {
        excesses: payload.excesses.find((e) => e.id === selectedExcess.id)
          ? [
              {
                ...(payload.excesses.find(
                  (e) => e.id === selectedExcess.id
                ) as WireMotorExcess),
                selected: true,
              },
            ]
          : [],
      }
    : {};

  return {
    ...requestDefaults(WireProduct.Motor),
    parties: toPolicyDetailsParties(state),
    addresses: toWireAddresses(state),
    flybuys: toWireFlybuys(isFlybuysMember(state), getFlybuysNumber(state)),
    coverType: wireCoverType[selectedCoverType],
    policy: {
      ...payload.policy,
      ...toWireMotorPolicy(state, policyStartDate),
    },
    coverages: toWireCoverages(
      state,
      selectedInsuranceType,
      selectedExtras,
      selectedExcess
    ).map((coverage) => ({
      ...coverage,
      sumInsured: {
        type: wireSumInsuredType[marketOrAgreedValue],
        amount: parseInt(agreedVehicleValue, 10),
      },
    })),
    vehicle: isVehicleManualEntry(state)
      ? manualToWireVehicle(
          state,
          getVehicleManuallyEntered(state),
          wireCoverageRefIds[selectedInsuranceType]
        )
      : lookupToWireVehicle(
          state,
          getVehicleFromSearch(state),
          wireCoverageRefIds[selectedInsuranceType]
        ),
    ...excessUpdate,
  };
};

export const marshalQuoteUpdateSave = (
  funnelState: MotorFunnelState,
  emailAddress: string
) => {
  const saveQuoteRequest = {
    ...marshalQuoteUpdateRequest(
      funnelState,
      {
        ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      },
      getSelectedExtras(funnelState),
      getSelectedCoverType(funnelState),
      getPolicyStartDate(funnelState)
    ),
    targetStatus: WireStatus.Save,
  };
  saveQuoteRequest.policy.otherAttributes.push(
    toWireSaveQuoteEmailAddress(emailAddress)
  );

  return saveQuoteRequest;
};

export const marshalQuoteUpdateEmail = (
  funnelState: MotorFunnelState,
  emailAddress: string
) => {
  const saveQuoteRequest = {
    ...marshalQuoteUpdateRequest(
      funnelState,
      {
        ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      },
      getSelectedExtras(funnelState),
      getSelectedCoverType(funnelState),
      getPolicyStartDate(funnelState)
    ),
    targetStatus: WireStatus.Email,
  };
  saveQuoteRequest.policy.otherAttributes.push(
    toWireSaveQuoteEmailAddress(emailAddress)
  );

  return saveQuoteRequest;
};

export const marshalQuoteUpdateDetails = (
  funnelState: MotorFunnelState
): WireQuoteRequest =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    optionalExtras(funnelState),
    getSelectedCoverType(funnelState),
    getPolicyStartDate(funnelState)
  );

export const marshalQuoteUpdateExcess = (
  funnelState: MotorFunnelState,
  selectedExcess: Excess
): WireQuoteRequest =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      excess: selectedExcess,
    },
    getSelectedExtras(funnelState),
    getSelectedCoverType(funnelState),
    getPolicyStartDate(funnelState)
  );

export const marshalQuoteUpdateVehicleValue = (
  funnelState: MotorFunnelState,
  agreedVehicleValue: string,
  marketOrAgreedValue: MarketAgreedValue
) =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      agreedVehicleValue,
      marketOrAgreedValue,
    },
    getSelectedExtras(funnelState),
    getSelectedCoverType(funnelState),
    getPolicyStartDate(funnelState)
  );

export const marshalQuoteUpdatePolicyStartDate = (
  funnelState: MotorFunnelState,
  policyStartDate: string
) =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    getSelectedExtras(funnelState),
    getSelectedCoverType(funnelState),
    policyStartDate
  );

export const marshalQuoteUpdateInsuranceType = (
  funnelState: MotorFunnelState,
  insuranceType: MotorInsuranceType,
  coverType: MotorCoverType,
  removeExtras?: Array<OptionalExtra>
) => {
  const alreadySelectedExtras = getSelectedExtras(funnelState);
  const selectedExtras = removeExtras
    ? alreadySelectedExtras.filter(
        (extra) => !removeExtras.includes(extra as OptionalExtra)
      )
    : alreadySelectedExtras;

  return marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      excess: { id: '', type: ExcessType.Standard, amount: 0 },
      insuranceType,
    },
    selectedExtras,
    coverType,
    getPolicyStartDate(funnelState)
  );
};
export const marshalQuoteUpdateInsuranceOption = (
  funnelState: MotorFunnelState,
  coverType: MotorCoverType
) =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      excess: { id: '', type: ExcessType.Standard, amount: 0 },
    },
    getSelectedExtras(funnelState),
    coverType,
    getPolicyStartDate(funnelState)
  );

export const marshalQuoteUpdateOptionalExtras = (
  funnelState: MotorFunnelState,
  selectedExtras: string[]
) =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    selectedExtras,
    getSelectedCoverType(funnelState),
    getPolicyStartDate(funnelState)
  );

const marshalQuoteUpdateWithMetadata = (
  funnelState: MotorFunnelState,
  targetStatus: WireStatus,
  metaData: WireNameValue[]
) => ({
  ...marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    getSelectedExtras(funnelState),
    getSelectedCoverType(funnelState),
    getPolicyStartDate(funnelState)
  ),
  targetStatus,
  metaData,
});

export const marshalQuoteUpdateToPolicyDetails = (
  funnelState: MotorFunnelState
) =>
  marshalQuoteUpdateWithMetadata(
    funnelState,
    WireStatus.PolicyDetails,
    getPolicyDetailsMetadata()
  );

export const marshalQuoteUpdateToDataComplete = (
  funnelState: MotorFunnelState
) =>
  marshalQuoteUpdateWithMetadata(
    funnelState,
    WireStatus.DataComplete,
    getQuoteCompleteMetadata()
  );

const toPreselectedCoverType = (
  quote: WireMotorQuotePayload
): MotorCoverType => {
  switch (quote.coverType) {
    case WireCoverType.ThirdPartyFireAndTheft:
      return MotorCoverType.ThirdPartyFireAndTheft;
    case WireCoverType.ThirdPartyPropertyDamage:
      return MotorCoverType.ThirdPartyPropertyDamage;
    case WireCoverType.Comprehensive:
      return MotorCoverType.Comprehensive;
    default:
      return MotorCoverType.Unspecified;
  }
};

const toPreselectedInsuranceType = (
  quote: WireMotorQuotePayload
): MotorInsuranceType =>
  [
    WireCoverType.ThirdPartyFireAndTheft,
    WireCoverType.ThirdPartyPropertyDamage,
  ].includes(quote.coverType)
    ? MotorInsuranceType.ThirdParty
    : MotorInsuranceType.Comprehensive;

export const toQuoteDetails = (
  quoteDetails: ApiData<MotorQuoteDetails>
): MotorQuoteData => {
  const { payload } = getApiData(quoteDetails);
  const { addresses, flybuys, policy, campaigns } = payload;

  return {
    ...mapMainDriver(payload),
    preselectedCoverType: toPreselectedCoverType(payload),
    preselectedInsuranceType: toPreselectedInsuranceType(payload),
    prefilledCampaignId: '',
    prefilledPromoCode: '',
    colesCampaignCode: '',
    overnightParkingAddress: toMainAddress(addresses),
    recentInsurer: toRecentInsurer(policy),
    coverLevel: toCoverLevel(payload),
    isUsedForBusiness: toIsUsedForBusiness(payload),
    businessType: toBusinessType(payload),
    isSmallBusiness: toSmallBusiness(policy),
    finance: toFinance(payload),
    vehicleFinancier: toVehicleFinancier(payload),
    policyStartDate: toPolicyStartDate(policy),
    additionalDrivers: toAdditionalDrivers(payload),
    vehicleSearchType: toVehicleSearchType(payload),
    licencePlate: toLicencePlate(payload),
    vehicleFromSearch: toVehicleFromSearch(payload),
    vehicleManualSelectFields: toVehicleManualSelectFields(payload),
    vehicleManuallyEntered: toVehicleManuallyEntered(payload),
    hasFlybuys: toHasFlybuys(flybuys),
    flybuysNumber: toFlybuysNumber(flybuys),
    promoCode: toPromoCode(campaigns),
    coverForDriversUnder25: toCoverForDriversUnder25(payload),
    addDriver: toAddDriver(payload),
  };
};

export const toReviewDetails = (
  quoteDetails: ApiData<MotorQuoteDetails>
): ReviewDetailsData => {
  const { payload } = getApiData(quoteDetails);

  return {
    policyStartDate: toValidPolicyStartDate(payload),
    mainDriver: mapMainDriver(payload),
    additionalDrivers: toAdditionalDrivers(payload),
  };
};

export const toSelectedExtras = (
  quoteDetails: ApiData<MotorQuoteDetails>
): OptionalExtra[] => {
  const { payload } = getApiData(quoteDetails);
  return findSelectedExtras(
    payload.premiums,
    wireCoverageId[payload.coverType]
  ).map((extra) => toOptionalExtra(extra.name as WireCoverageOptionType));
};
